import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
    useUserDirectory, 
    useConfigFile, 
    useSaveConfigFile 
} from '../../../hooks/useApi';

/**
 * 系統設定頁面
 * 管理用戶角色、審批群組、權限配置等
 */
const Settings = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('users');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // 用戶目錄
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    // React Query hooks
    const { 
        data: directoryResult, 
        isLoading: loadingUsers, 
        refetch: refetchUsers 
    } = useUserDirectory();

    const { 
        data: configResult, 
        refetch: refetchConfig 
    } = useConfigFile('config/permissions.json');

    const saveMutation = useSaveConfigFile();

    const userDirectory = directoryResult?.data?.users || [];
    const permissionsConfig = configResult?.data?.content;
    const loading = loadingUsers;
    const saving = saveMutation.isPending;

    // 檢查權限
    const isAdmin = user?.admin || user?.roles?.includes('admin');

    // 儲存權限配置
    const savePermissionsConfig = async (config) => {
        try {
            setError(null);
            await saveMutation.mutateAsync({
                path: 'config/permissions.json',
                content: JSON.stringify({
                    ...config,
                    metadata: {
                        ...config.metadata,
                        updatedAt: new Date().toISOString().split('T')[0]
                    }
                }, null, 2)
            });

            setSuccessMessage('設定已儲存');
            refetchConfig();
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError('儲存失敗: ' + (err.message || '未知錯誤'));
        }
    };

    // 搜尋用戶
    const filteredUsers = userDirectory.filter(u => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            u.displayName?.toLowerCase().includes(term) ||
            u.mail?.toLowerCase().includes(term) ||
            u.department?.toLowerCase().includes(term) ||
            u.jobTitle?.toLowerCase().includes(term)
        );
    });

    // 獲取用戶的角色
    const getUserRoles = (userId) => {
        const assignment = permissionsConfig?.userAssignments?.users?.[userId];
        return assignment?.roles || [];
    };

    // 獲取用戶所屬的群組
    const getUserGroups = (userId) => {
        if (!permissionsConfig?.approvalGroups) return [];
        const groups = [];
        Object.entries(permissionsConfig.approvalGroups).forEach(([groupId, group]) => {
            if (group.members?.some(m => m.userId === userId || m.email === userId)) {
                groups.push(groupId);
            }
        });
        return groups;
    };

    // 更新用戶角色
    const updateUserRoles = async (userId, email, roles) => {
        const newConfig = {
            ...permissionsConfig,
            userAssignments: {
                ...permissionsConfig.userAssignments,
                users: {
                    ...permissionsConfig.userAssignments?.users,
                    [email]: {
                        ...permissionsConfig.userAssignments?.users?.[email],
                        userId,
                        roles
                    }
                }
            }
        };
        await savePermissionsConfig(newConfig);
    };

    // 添加成員到群組
    const addMemberToGroup = async (groupId, member) => {
        const group = permissionsConfig.approvalGroups[groupId];
        if (!group) return;

        const newMembers = [...(group.members || [])];
        if (!newMembers.some(m => m.userId === member.id)) {
            newMembers.push({
                userId: member.id,
                displayName: member.displayName,
                email: member.mail || member.userPrincipalName,
                jobTitle: member.jobTitle,
                department: member.department,
                isActive: true
            });
        }

        const newConfig = {
            ...permissionsConfig,
            approvalGroups: {
                ...permissionsConfig.approvalGroups,
                [groupId]: {
                    ...group,
                    members: newMembers
                }
            }
        };
        await savePermissionsConfig(newConfig);
    };

    // 從群組移除成員
    const removeMemberFromGroup = async (groupId, memberId) => {
        const group = permissionsConfig.approvalGroups[groupId];
        if (!group) return;

        const newMembers = (group.members || []).filter(m => m.userId !== memberId);

        const newConfig = {
            ...permissionsConfig,
            approvalGroups: {
                ...permissionsConfig.approvalGroups,
                [groupId]: {
                    ...group,
                    members: newMembers
                }
            }
        };
        await savePermissionsConfig(newConfig);
    };

    // 添加成員到群組
    if (!isAdmin) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <h2 className="text-xl font-bold text-red-800 mb-2">🔒 存取被拒絕</h2>
                    <p className="text-red-600">您需要管理員權限才能存取此頁面。</p>
                </div>
            </div>
        );
    }

    // Tab 按鈕
    const TabButton = ({ id, label, icon }) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === id
                    ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
        >
            {icon} {label}
        </button>
    );

    // 用戶卡片
    const UserCard = ({ userData, onClick, selected }) => (
        <div
            onClick={() => onClick(userData)}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
                selected
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
            }`}
        >
            <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold mr-3">
                    {userData.displayName?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{userData.displayName}</p>
                    <p className="text-sm text-gray-500 truncate">{userData.mail}</p>
                </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
                {userData.jobTitle && <span className="mr-2">{userData.jobTitle}</span>}
                {userData.department && <span>| {userData.department}</span>}
            </div>
            {/* 角色標籤 */}
            <div className="mt-2 flex flex-wrap gap-1">
                {getUserRoles(userData.mail).map(role => (
                    <span key={role} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                        {permissionsConfig?.systemRoles?.[role]?.name || role}
                    </span>
                ))}
                {getUserGroups(userData.id).map(group => (
                    <span key={group} className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                        {permissionsConfig?.approvalGroups?.[group]?.groupName || group}
                    </span>
                ))}
            </div>
        </div>
    );

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* 標題 */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <span className="mr-2">⚙️</span>
                    系統設定
                </h1>
                <p className="text-gray-600 mt-1">
                    管理用戶角色、審批群組和權限配置
                </p>
            </div>

            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Link
                    to="/settings/templates"
                    className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                    <div>
                        <div className="text-sm font-semibold text-blue-800">信件模板維護</div>
                        <div className="text-xs text-blue-600">管理通知郵件模板內容</div>
                    </div>
                    <span className="text-blue-700">→</span>
                </Link>
            </div>

            {/* 訊息提示 */}
            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">❌ {error}</p>
                    <button onClick={() => setError(null)} className="text-sm text-red-600 hover:underline mt-2">
                        關閉
                    </button>
                </div>
            )}
            {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800">✅ {successMessage}</p>
                </div>
            )}

            {/* 分頁標籤 */}
            <div className="flex space-x-1 mb-0 border-b border-gray-200">
                <TabButton id="users" label="用戶管理" icon="👥" />
                <TabButton id="groups" label="審批群組" icon="🏢" />
                <TabButton id="roles" label="系統角色" icon="🔐" />
            </div>

            {/* 內容區 */}
            <div className="bg-white rounded-b-lg rounded-tr-lg shadow-md p-6 border border-gray-200 border-t-0">
                
                {/* 用戶管理 */}
                {activeTab === 'users' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* 用戶列表 */}
                        <div className="lg:col-span-2">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Entra ID 用戶目錄</h3>
                                <button
                                    onClick={() => refetchUsers()}
                                    className="text-sm text-blue-600 hover:underline"
                                    disabled={loading}
                                >
                                    🔄 重新載入
                                </button>
                            </div>

                            {/* 搜尋框 */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="搜尋用戶（姓名、Email、部門、職稱）..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* 用戶網格 */}
                            {loading ? (
                                <div className="text-center py-8 text-gray-500">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                    載入中...
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    {searchTerm ? '找不到符合的用戶' : '沒有用戶資料'}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-auto">
                                    {filteredUsers.slice(0, 50).map(u => (
                                        <UserCard
                                            key={u.id}
                                            userData={u}
                                            onClick={setSelectedUser}
                                            selected={selectedUser?.id === u.id}
                                        />
                                    ))}
                                    {filteredUsers.length > 50 && (
                                        <p className="col-span-2 text-center text-gray-500 py-2">
                                            顯示前 50 筆結果，共 {filteredUsers.length} 筆
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 用戶詳情/編輯面板 */}
                        <div>
                            {selectedUser ? (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 sticky top-4">
                                    <h4 className="font-semibold text-gray-900 mb-4">用戶設定</h4>
                                    
                                    {/* 用戶資訊 */}
                                    <div className="mb-4 pb-4 border-b border-gray-200">
                                        <div className="flex items-center mb-2">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold mr-3">
                                                {selectedUser.displayName?.charAt(0) || '?'}
                                            </div>
                                            <div>
                                                <p className="font-medium">{selectedUser.displayName}</p>
                                                <p className="text-sm text-gray-500">{selectedUser.mail}</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            {selectedUser.jobTitle} | {selectedUser.department}
                                        </p>
                                    </div>

                                    {/* 角色設定 */}
                                    <div className="mb-4">
                                        <h5 className="font-medium text-gray-700 mb-2">系統角色</h5>
                                        <div className="space-y-2">
                                            {Object.entries(permissionsConfig?.systemRoles || {}).map(([roleId, role]) => (
                                                <label key={roleId} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={getUserRoles(selectedUser.mail).includes(roleId)}
                                                        onChange={(e) => {
                                                            const currentRoles = getUserRoles(selectedUser.mail);
                                                            const newRoles = e.target.checked
                                                                ? [...currentRoles, roleId]
                                                                : currentRoles.filter(r => r !== roleId);
                                                            updateUserRoles(selectedUser.id, selectedUser.mail, newRoles);
                                                        }}
                                                        className="mr-2"
                                                        disabled={saving}
                                                    />
                                                    <span className="text-sm">{role.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 所屬群組 */}
                                    <div>
                                        <h5 className="font-medium text-gray-700 mb-2">審批群組</h5>
                                        <div className="space-y-2">
                                            {Object.entries(permissionsConfig?.approvalGroups || {}).map(([groupId, group]) => {
                                                const isMember = group.members?.some(m => m.userId === selectedUser.id);
                                                return (
                                                    <label key={groupId} className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isMember}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    addMemberToGroup(groupId, selectedUser);
                                                                } else {
                                                                    removeMemberFromGroup(groupId, selectedUser.id);
                                                                }
                                                            }}
                                                            className="mr-2"
                                                            disabled={saving}
                                                        />
                                                        <span className="text-sm">{group.groupName}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-8 border border-gray-200 text-center text-gray-500">
                                    <p className="text-4xl mb-2">👤</p>
                                    <p>選擇一個用戶來查看和編輯設定</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 審批群組 */}
                {activeTab === 'groups' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">審批群組配置</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(permissionsConfig?.approvalGroups || {}).map(([groupId, group]) => (
                                <div key={groupId} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-gray-900">{group.groupName}</h4>
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                            {group.approvalMode === 'parallel' ? '並行' : '單一'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3">{group.description}</p>
                                    
                                    {/* 成員列表 */}
                                    <div className="border-t border-gray-200 pt-3">
                                        <p className="text-xs text-gray-500 mb-2">
                                            成員 ({group.members?.length || 0})
                                        </p>
                                        {group.members?.length > 0 ? (
                                            <div className="space-y-1">
                                                {group.members.map(member => (
                                                    <div key={member.userId} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
                                                        <span>{member.displayName}</span>
                                                        <button
                                                            onClick={() => removeMemberFromGroup(groupId, member.userId)}
                                                            className="text-red-500 hover:text-red-700"
                                                            disabled={saving}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-400 text-sm italic">尚無成員</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 系統角色 */}
                {activeTab === 'roles' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">系統角色定義</h3>
                        </div>

                        <div className="space-y-4">
                            {Object.entries(permissionsConfig?.systemRoles || {}).map(([roleId, role]) => (
                                <div key={roleId} className="bg-white rounded-lg border border-gray-200 p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center">
                                            <span className={`w-3 h-3 rounded-full mr-3 ${
                                                roleId === 'admin' ? 'bg-red-500' :
                                                roleId === 'manager' ? 'bg-blue-500' :
                                                roleId === 'finance' ? 'bg-green-500' :
                                                'bg-gray-400'
                                            }`}></span>
                                            <h4 className="font-semibold text-gray-900">{role.name}</h4>
                                        </div>
                                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{roleId}</code>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {role.permissions?.map((perm, idx) => (
                                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                                {perm}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-2 flex gap-2 text-xs">
                                        {role.canAccessDevPanel && (
                                            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">可存取偵錯面板</span>
                                        )}
                                        {role.canAccessSettings && (
                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">可存取設定</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;
